import { GlobalStore } from '@highlight/ipc'
import { AttachmentPermission, AttachmentTypes } from '@shared/types/Attachments.ts'
import { isWindows } from '@shared/utils/platform.ts'
import { isAlphaOrBetaChannel } from '@shared/utils/builds/channels.shared'
import { LEGACY_ONBOARDED_FLAG } from '@shared/onboarding/onboarding-flags'
import {
  LEGACY_SIDEPANEL_HEIGHT_SETTING_KEY,
  LEGACY_SIDEPANEL_VERTICAL_OFFSET_SETTING_KEY,
  LEGACY_SIDEPANEL_WIDTH_SETTING_KEY,
  SIDEPANEL_GEOMETRY_SETTING_KEY,
  type SidepanelDisplayGeometry,
  type SidepanelGeometryStore,
} from '@shared/sidepanel/display-geometry'
import {
  LEGACY_MINI_POSITION_SETTING_KEY,
  MINI_POSITION_SETTING_KEY,
  type MiniPosition,
  type MiniPositionStore,
} from '@shared/mini/display-position'

export const DEFAULT_WINDOWS_TRIGGER_HOTKEY = 'Alt + .'
export const DEFAULT_MAC_TRIGGER_HOTKEY = 'Cmd + .'

export const DEFAULT_WINDOWS_TASK_CREATION_HOTKEY = 'Ctrl + Shift + Space'
export const DEFAULT_MAC_TASK_CREATION_HOTKEY = 'Cmd + Shift + Space'

export const getDefaultTriggerHotkey = () => {
  if (isWindows) {
    return DEFAULT_WINDOWS_TRIGGER_HOTKEY
  } else {
    return DEFAULT_MAC_TRIGGER_HOTKEY
  }
}

export const DEFAULT_VOICE_HOTKEY = 'Alt'

export const getDefaultVoiceHotkey = () => DEFAULT_VOICE_HOTKEY

export const getDefaultTaskCreationHotkey = () => {
  if (isWindows) {
    return DEFAULT_WINDOWS_TASK_CREATION_HOTKEY
  } else {
    return DEFAULT_MAC_TASK_CREATION_HOTKEY
  }
}

export const getTriggerHotkey = () => {
  const hotkey = GlobalStore.settings().get<string>('triggerHotkey')
  if (hotkey) {
    return hotkey
  }
  return getDefaultTriggerHotkey()
}

export type HotkeySettingKey = 'triggerHotkey'

/**
 * Get all currently registered user hotkeys.
 * Used for duplicate hotkey validation.
 */
export const getAllRegisteredHotkeys = (): Record<HotkeySettingKey, string> => {
  return {
    triggerHotkey: getTriggerHotkey(),
  }
}

/**
 * Check if a hotkey is already in use by another setting.
 * @param hotkey The hotkey to check
 * @param excludeKey The setting key to exclude from the check (the setting being edited)
 * @returns The key of the conflicting setting, or null if no conflict
 */
export const getConflictingHotkeyKey = (hotkey: string, excludeKey: HotkeySettingKey): HotkeySettingKey | null => {
  if (!hotkey) return null

  const allHotkeys = getAllRegisteredHotkeys()
  for (const [key, value] of Object.entries(allHotkeys)) {
    if (key !== excludeKey && value === hotkey) {
      return key as HotkeySettingKey
    }
  }
  return null
}

export const isDefaultTriggerHotkey = () => {
  return getTriggerHotkey() === getDefaultTriggerHotkey()
}

export const setTriggerHotkey = (hotkey: string) => GlobalStore.settings().set('triggerHotkey', hotkey)

export const getTaskCreationHotkey = () => {
  const hotkey = GlobalStore.settings().get<string>('taskCreationHotkey')
  if (hotkey) {
    return hotkey
  }
  return getDefaultTaskCreationHotkey()
}

export const isDefaultTaskCreationHotkey = () => {
  return getTaskCreationHotkey() === getDefaultTaskCreationHotkey()
}

export const isDeveloperModeEnabled = () => {
  return GlobalStore
}

/**
 * Deprecated in favor of attachment permissions system.
 * See: `canNotify` and `canDetect`
 * @deprecated
 */
export const isInstantScreenshotsEnabled = () => {
  return GlobalStore.settings().get('instant-screenshots') ?? true
}

/// ////////////////////////
// Attachments Functions //
/// ////////////////////////

/**
 * Return the default {@link AttachmentPermission} for the specified {@link AttachmentTypes}
 * @param attachmentType
 */
export const getDefaultAttachmentPermission = (attachmentType: AttachmentTypes): AttachmentPermission => {
  switch (attachmentType) {
    case 'clipboard-text':
      return 'detect'
    case 'window-contents':
      return 'detect'
    case 'screenshots':
      return 'detect'
    case 'selected-text':
      return 'detect'
  }
}

/**
 * Return current {@link AttachmentPermission} for the specified {@link AttachmentTypes}. Returns the default if none.
 * @param attachmentType
 */
export const getAttachmentPermission = (attachmentType: AttachmentTypes) => {
  return (
    GlobalStore.settings().get<AttachmentPermission>(`attachmentAndDetectionPermissions.${attachmentType}`) ??
    getDefaultAttachmentPermission(attachmentType)
  )
}

/**
 * Set the current {@link AttachmentPermission} for an {@link AttachmentTypes}.
 * @param attachmentType
 * @param permission
 */
export const setAttachmentPermission = (attachmentType: AttachmentTypes, permission: AttachmentPermission) => {
  GlobalStore.settings().set(`attachmentAndDetectionPermissions.${attachmentType}`, permission)
}

const detectionTypes: AttachmentPermission[] = ['detect']
/**
 * Return true if the specified {@link AttachmentTypes} has the permission to be detected automatically.
 * @param attachmentType
 */
export const canDetect = (attachmentType: AttachmentTypes) => {
  return detectionTypes.includes(getAttachmentPermission(attachmentType))
}

/**
 * Return true if attachment notifications are enabled globally.
 */
export const areAttachmentNotificationsEnabled = () => {
  return GlobalStore.settings().get<boolean>('attachmentNotifications.enabled') ?? true
}

/**
 * Set the global attachment notifications enabled state.
 */
export const setAttachmentNotificationsEnabled = (enabled: boolean) => {
  GlobalStore.settings().set('attachmentNotifications.enabled', enabled)
}

/**
 * Return true if the specified {@link AttachmentTypes} has the permission to be detected automatically and notify the user that it has been detected.
 * @param attachmentType
 */
export const canNotify = (attachmentType: AttachmentTypes) => {
  return canDetect(attachmentType) && areAttachmentNotificationsEnabled()
}

/**
 * Migrates old settings for screenshot detection, instant screenshots, and audio transcriber to the new
 * attachment permissions system.
 */
export const migrateAttachmentPermissions = () => {
  const migrated = GlobalStore.settings().get('migrations.attachmentPermissions')
  if (migrated) {
    return
  }

  // Migrate instant screenshots to new permissions.
  // For this, if the user previously had it disabled, we will reset it to detect (but not attach).
  // UNLESS, the user previously had detection disabled. Then this will just be locked.
  setAttachmentPermission(
    'screenshots',
    isInstantScreenshotsEnabled()
      ? 'attach'
      : GlobalStore.settings().get('screenshot.highlight') === false
        ? 'locked'
        : 'detect',
  )

  // NOTE: Screen Transcriber toggle was not previously hooked up. Nothing to migrate.
  // NOTE: Clipboard Text is a new permission. Nothing to migrate.

  // Set the attachment permissions migrations flag to avoid re-running this.
  GlobalStore.settings().set('migrations.attachmentPermissions', true)
}

/**
 * Resets the selected text permission to enabled for alpha and beta users.
 */
export const resetSelectedTextPermission = () => {
  const reset = GlobalStore.settings().get('migrations.selectedTextPermissionReset')
  if (reset) {
    return
  }

  if (isAlphaOrBetaChannel()) {
    console.log('resetting selected text permission')
    setAttachmentPermission('selected-text', 'detect')
    setAttachmentNotificationsEnabled(true)
  }

  GlobalStore.settings().set('migrations.selectedTextPermissionReset', true)
}

/**
 * Resets the selected text permission to enabled for production/latest users.
 */
export const resetSelectedTextPermissionForLatest = () => {
  const reset = GlobalStore.settings().get('migrations.selectedTextPermissionResetLatest')
  if (reset) {
    return
  }

  if (!isAlphaOrBetaChannel()) {
    console.log('resetting selected text permission for latest users')
    setAttachmentPermission('selected-text', 'detect')
    setAttachmentNotificationsEnabled(true)
  }

  GlobalStore.settings().set('migrations.selectedTextPermissionResetLatest', true)
}

const migrateToNotifyAttachmentPermissions = () => {
  const migrated = GlobalStore.settings().get('migrations.notifyAttachmentPermissions')
  if (migrated) {
    return
  }

  console.log('Migrating to notify attachment permissions')
  const attachmentTypes: AttachmentTypes[] = ['clipboard-text', 'window-contents', 'screenshots', 'selected-text']

  type LegacyAttachmentPermission = 'locked' | 'detect' | 'attach'

  // Attach is no longer a supported attachment permission and a new attachment permission called "notify" was added.
  // Attachment settings will now be stored in the global state variable "attachmentAndDetectionPermissions" to preserve
  // the user's original settings to make these changes backwards compatible.
  //
  // If the existing permission is detect or attach, set the new setting to notify to maintain the same behavior
  // that existed prior to this change.
  let shouldEnableNotifications = false
  attachmentTypes.forEach((type) => {
    const legacyPermission = GlobalStore.settings().get<LegacyAttachmentPermission>(`attachmentPermissions.${type}`)
    if (legacyPermission === 'attach' || legacyPermission === 'detect') {
      setAttachmentPermission(type, 'detect')
      shouldEnableNotifications = true
    } else if (legacyPermission === 'locked') {
      setAttachmentPermission(type, 'locked')
    } else {
      setAttachmentPermission(type, getDefaultAttachmentPermission(type))
    }
  })

  if (shouldEnableNotifications) {
    setAttachmentNotificationsEnabled(true)
  }

  GlobalStore.settings().set('migrations.notifyAttachmentPermissions', true)
}

const migrateAudioFeatureSettings = () => {
  const migrated = GlobalStore.settings().get('migrations.migrateAudioFeatureSettings')
  if (migrated) {
    return
  }

  if (GlobalStore.user().get(LEGACY_ONBOARDED_FLAG)) {
    const oldAudioNoteEnabled =
      GlobalStore.settings().get<AttachmentPermission>('attachmentAndDetectionPermissions.audio-transcript') ?? 'locked'

    const oldPushToTalkEnabled =
      GlobalStore.settings().get<AttachmentPermission>('attachmentAndDetectionPermissions.ptt') ?? 'locked'

    // If either audio notes or PTT was enabled, set audio features enabled to true.
    // If both were locked, set audio features enabled to false.
    if (oldAudioNoteEnabled === 'locked' && oldPushToTalkEnabled === 'locked') {
      GlobalStore.settings().set('audio-features-enabled', false)
    } else {
      GlobalStore.settings().set('audio-features-enabled', true)
      if (oldPushToTalkEnabled !== 'locked') {
        GlobalStore.settings().set('push-to-talk-enabled', true)
      }
    }
  }

  GlobalStore.settings().set('migrations.migrateAudioFeatureSettings', true)
}

const migrateNotifyToGlobalNotifications = () => {
  const migrated = GlobalStore.settings().get('migrations.migrateNotifyToGlobalNotifications')
  if (migrated) {
    return
  }

  console.log('Migrating notify permissions to global notifications')
  const attachmentTypes: AttachmentTypes[] = ['clipboard-text', 'window-contents', 'screenshots', 'selected-text']

  // Check if any attachment type has 'notify' permission
  let hasAnyNotifyPermission = false

  attachmentTypes.forEach((type) => {
    const currentPermission = GlobalStore.settings().get<string>(`attachmentAndDetectionPermissions.${type}`)
    if (currentPermission === 'notify') {
      hasAnyNotifyPermission = true
      // Change 'notify' to 'detect'
      setAttachmentPermission(type, 'detect')
    }
  })

  // If any attachment had notify permission, enable global notifications
  setAttachmentNotificationsEnabled(hasAnyNotifyPermission)

  GlobalStore.settings().set('migrations.migrateNotifyToGlobalNotifications', true)
}

const migrateAutomaticUpdatesDefaultEnabled = () => {
  const migrated = GlobalStore.settings().get('migrations.automaticUpdatesDefaultEnabled')
  if (migrated) {
    return
  }

  // Automatic updates are now on by default. Enable for existing users who never explicitly
  // set the toggle; users who explicitly disabled it (key present and false) keep their choice.
  if (GlobalStore.settings().get('updates.automatic') === undefined) {
    GlobalStore.settings().set('updates.automatic', true)
  }

  GlobalStore.settings().set('migrations.automaticUpdatesDefaultEnabled', true)
}

/**
 * Sidepanel geometry and Mini's position used to be single display-independent
 * values; both are now maps keyed by monitor. The old values become the
 * `inherited` layer rather than being bound to whichever display happens to be
 * attached at migration time — so they still apply everywhere until the user
 * sizes or parks on a specific screen.
 */
const migrateOverlayGeometryPerDisplay = () => {
  const migrated = GlobalStore.settings().get('migrations.overlayGeometryPerDisplay')
  if (migrated) {
    return
  }

  const legacyGeometry: Array<[keyof SidepanelDisplayGeometry, string]> = [
    ['width', LEGACY_SIDEPANEL_WIDTH_SETTING_KEY],
    ['height', LEGACY_SIDEPANEL_HEIGHT_SETTING_KEY],
    ['verticalOffset', LEGACY_SIDEPANEL_VERTICAL_OFFSET_SETTING_KEY],
  ]

  const inherited: SidepanelDisplayGeometry = {}
  for (const [field, key] of legacyGeometry) {
    const value = GlobalStore.settings().get<number>(key)
    if (typeof value === 'number' && Number.isFinite(value)) {
      inherited[field] = value
    }
    GlobalStore.settings().delete(key)
  }

  if (Object.keys(inherited).length > 0) {
    GlobalStore.settings().set<SidepanelGeometryStore>(SIDEPANEL_GEOMETRY_SETTING_KEY, { inherited })
  }

  // Only the current `{ x, y }` shape carries over; a much older `{ right, y }`
  // format can't be converted without the window width main doesn't have here,
  // and falls back to Mini's default placement.
  const legacyPosition = GlobalStore.settings().get<MiniPosition>(LEGACY_MINI_POSITION_SETTING_KEY)
  if (
    legacyPosition &&
    typeof legacyPosition.x === 'number' &&
    Number.isFinite(legacyPosition.x) &&
    typeof legacyPosition.y === 'number' &&
    Number.isFinite(legacyPosition.y)
  ) {
    GlobalStore.settings().set<MiniPositionStore>(MINI_POSITION_SETTING_KEY, {
      inherited: { x: legacyPosition.x, y: legacyPosition.y },
    })
  }
  GlobalStore.settings().delete(LEGACY_MINI_POSITION_SETTING_KEY)

  GlobalStore.settings().set('migrations.overlayGeometryPerDisplay', true)
}

export const migrateSettings = () => {
  migrateAttachmentPermissions()
  resetSelectedTextPermission()
  resetSelectedTextPermissionForLatest()
  migrateToNotifyAttachmentPermissions()
  migrateAudioFeatureSettings()
  migrateNotifyToGlobalNotifications()
  migrateAutomaticUpdatesDefaultEnabled()
  migrateOverlayGeometryPerDisplay()
}
